<template>
  <div style="width: 100%; position: relative">
    <ckeditor
      v-model="editorModel"
      :editor="editor"
      :ref="name"
      :name="name"
      :config="editorConfig"
      :autoFocus="autofocus"
      :disabled="disabled"
      @keydown.native="leaveComment"
      @keyup.native="countTextLength"
      @blur="$emit('blur')"
      class="el-textarea__inner"
      :class="{ 'white-resize-icon': whiteResizeIcon }"
    />
    <span
      v-if="maxLength && textLength && showLength"
      :class="['text-limit', disabled ? 'is-disabled' : '']"
    >
      {{ `${textLength}/${maxLength}` }}
    </span>
  </div>
</template>

<script>
import { mixin } from '@/utils/mixins';
import BalloonEditor from 'ckeditor5-build-balloon-underline';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import '@ckeditor/ckeditor5-basic-styles/build/translations/ru';
import '@ckeditor/ckeditor5-list/build/translations/ru';

export default {
  mixins: [mixin],
  components: {
    ckeditor: CKEditor.component
  },
  props: {
    autofocus: {
      type: Boolean,
      default: false
    },
    name: String,
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showLength: {
      type: Boolean,
      default: true
    },
    placeholder: String,
    minLength: Number,
    maxLength: Number,
    whiteResizeIcon: Boolean
  },
  data() {
    return {
      editor: BalloonEditor,
      editorConfig: {
        placeholder: this.placeholder,
        toolbar: ['bold', 'italic', 'underline', 'bulletedList'],
        language: 'ru'
      },
      textLength: 0,
      pressKey: false
    };
  },

  computed: {
    editorModel: {
      set(value) {
        if (this.maxLength && this.textLength == this.maxLength) return false;
        this.$emit('change', value);
      },
      get() {
        return this.formatEditorHtml(this.value)
          .replace(/\\"/g, '')
          .replace(' class="cdx-underline"', '');
      }
    }
  },
  methods: {
    leaveComment(event) {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.$emit('leaveComment');
      }
    },
    formatEditorHtml(text) {
      if (typeof text === 'string' && text) {
        const replacement = [
          [/&quot;/g, '"'],
          [/&#039;/g, "'"],
          [/&#38;/g, '"'],
          [/&#96;/g, '`'],
          [/%3A/g, ':']
        ];
        return text
          .replace(...replacement[0])
          .replace(...replacement[1])
          .replace(...replacement[2])
          .replace(...replacement[3])
          .replace(...replacement[4]);
      }
      return text;
    },

    countTextLength() {
      if (!this.maxLength) return;

      let count = 0;
      let max = this.maxLength;

      this.$refs[this.name].$el.childNodes.forEach(node => {
        // подсчет длины текста без учета вложенности html-тегов
        const text = node.nodeType !== 3 ? node.innerText : node.nodeValue;
        count += text.length;

        if (count > max) {
          const num = max - count;

          if (node.innerText) {
            node.innerText = node.innerText.replace(
              node.innerText.slice(num),
              ''
            );
          }
          if (node.nodeValue) {
            node.nodeValue = node.nodeValue.replace(
              node.nodeValue.slice(num),
              ''
            );
          }
          count = max;
        }
      });

      this.textLength = count;

      if (count == max) {
        this.editorModel = this.$refs[this.name].$el.innerHTML;
        this.value = this.$refs[this.name].$el.innerHTML;
      }

      return count;
    }
  }
};
</script>

<style lang="scss" scoped>
.el-textarea__inner {
  padding: 0;
  border: none;

  &:hover::after {
    background-color: transparent !important;
  }

  &.white-resize-icon:hover::after {
    background-color: #f6f8f9 !important;
    color: #f6f8f9 !important;
  }
}
</style>

<style lang="scss">
.ck {
  background-color: $white;

  &:hover::after {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    width: 16px !important;
  }

  .ck-placeholder::before {
    color: $black-60;
  }

  & ~ .text-limit {
    position: absolute;
    bottom: 1px;
    left: 1px;
    text-align: right;
    line-height: 24px;
    background-color: $white;
    width: calc(100% - 32px);
    border-radius: 4px;
  }

  &.ck-focused,
  &.ck-blurred {
    border-radius: 4px !important;
    border: 1px solid $grey16 !important;
    box-shadow: none !important;
    min-height: 64px;
    max-height: 480px;
    overflow-y: auto;
    word-break: break-word;

    &:after {
      z-index: 2;
      content: '';
      width: 28px;
      height: 28px;
      display: inline-block;
      position: absolute;
      background-size: 24px 24px;
      background-repeat: no-repeat;
      bottom: 2px;
      right: 2px;
      background-color: transparent;
      border-radius: 4px;
      pointer-events: none;
    }

    &.white-resize-icon:after {
      background-color: white;
      bottom: 1px;
      right: 1px;
      color: $white !important;
      background-color: white;
    }

    &:focus {
      border-color: $blue-120 !important;
      border-radius: 4px;
    }

    &:invalid {
      border: 1px solid $red !important;
      border-radius: 4px;
    }
  }

  &.ck-read-only {
    background-color: $black-10;
    border: 1px solid $black-30 !important;

    &:hover {
      border: 1px solid $grey16 !important;
    }

    &:after {
      content: '';
      width: 28px;
      height: 28px;
      display: inline-block;
      position: absolute;
      background-size: 24px 24px;
      background-repeat: no-repeat;
      bottom: 1px;
      right: 1px;
      background-color: $black-10 !important;
      border-radius: 4px;
      pointer-events: none;
    }
  }

  &.ck-editor__editable_inline {
    padding: 8px 12px 24px 12px !important;

    p {
      margin: 0 !important;
    }

    & > p {
      margin: 0;
      font-size: 16px;
      line-height: 24px;
      color: $black-200;
    }
  }

  &.ck-reset_all,
  &.ck-reset_all *:not(.ck-tooltip):not(.ck-tooltip__text) {
    background: $blue-130;
    color: $white;
  }

  &.ck-balloon-panel_arrow_s:after,
  &.ck-balloon-panel_arrow_s:before {
    content: unset !important;
  }

  &.ck-button:hover,
  &.ck-button:hover > *:not(.ck-tooltip):not(.ck-tooltip__text) {
    background: $blue-150 !important;
  }

  .ck-balloon-rotator {
    border-radius: 4px !important;
  }

  ol,
  ul {
    list-style: inside;
  }

  .ck-icon {
    color: $blue-130 !important;
  }

  &:hover ~ .text-limit {
    background-color: $black-10;
  }
}
</style>
